<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useAppStateStore } from '@/stores/AppStateStore.js';
const appStateStore = useAppStateStore();

const props = defineProps({
    exitUrl: { type: String, required: true },
    generateUrl: { type: String, required: true },
    pollUrlPrefix: { type: String, required: true },
    conversationUrl: { type: String, required: true },
    newConversationUrl: { type: String, required: true },
    searchesUrl: { type: String, required: true },
    saveSearchUrl: { type: String, required: true },
    previewUrl: { type: String, required: true },
    searchCountUrl: { type: String, required: true },
    locationsUrl: { type: String, required: true },
    geocodeUrl: { type: String, required: true },
    conversationId: { type: [String, Number], default: null },
    hasLeadHunterCampaigns: { type: Boolean, default: false },
    initialMessage: { type: String, default: '' },
});

const messages = ref([]);
const context = ref({});
const currentConversationId = ref(props.conversationId);
const isGenerating = ref(false);
const pollTimer = ref(null);
const activePollConversationId = ref(null);
const savedSearches = ref([]);
const currentFilters = ref({});
const selectedSearch = ref(null);
const showArchivedSearches = ref(false);
const saveFormRequest = ref(0);

const cloneFilters = (filters) => JSON.parse(JSON.stringify(filters));

const loadConversation = async () => {
    try {
        const response = await fetch(props.conversationUrl);
        const data = await response.json();
        currentConversationId.value = data.id;
        messages.value = data.messages || [];
        context.value = data.context || {};

        if (data.context?.confirmed_filters) {
            currentFilters.value = data.context.confirmed_filters;
        }
    } catch (e) {
        console.error('Failed to load conversation', e);
    }
};

const searchListUrl = (archived = showArchivedSearches.value) => {
    const url = new URL(props.searchesUrl, window.location.origin);

    if (archived) {
        url.searchParams.set('archived', '1');
    } else {
        url.searchParams.delete('archived');
    }

    return url.toString();
};

const loadSearches = async (archived = showArchivedSearches.value) => {
    try {
        const response = await fetch(searchListUrl(archived));
        const data = await response.json();
        savedSearches.value = data.searches || [];
    } catch (e) {
        console.error('Failed to load searches', e);
    }
};

const sendMessage = async (text) => {
    if (isGenerating.value) return;

    messages.value.push({ role: 'user', content: text });
    isGenerating.value = true;

    try {
        const response = await fetch(props.generateUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector(
                    'meta[name="csrf-token"]'
                )?.content,
            },
            body: JSON.stringify({
                prompt: text,
                filters: currentFilters.value,
            }),
        });

        const data = await response.json();
        currentConversationId.value = data.conversation_id;

        if (data.status === 'budget_exceeded') {
            isGenerating.value = false;
            pushFailureMessage(data.status);
            return;
        }

        startPolling();
    } catch (e) {
        console.error('Failed to send message', e);
        isGenerating.value = false;
    }
};

const pushFailureMessage = (status) => {
    messages.value.push({
        role: 'assistant',
        content:
            status === 'budget_exceeded'
                ? _mft('lead-hunter:hugo.budgetExceeded')
                : _mft('lead-hunter:hugo.errorMessage'),
        budgetExceeded: status === 'budget_exceeded',
    });
};

const startPolling = () => {
    if (pollTimer.value) clearInterval(pollTimer.value);

    const conversationId = currentConversationId.value;
    activePollConversationId.value = conversationId;

    pollTimer.value = setInterval(async () => {
        try {
            const response = await fetch(
                `${props.pollUrlPrefix}${conversationId}/poll`
            );
            const data = await response.json();

            if (activePollConversationId.value !== conversationId) {
                return;
            }

            if (data.status === 'completed') {
                stopPolling();
                isGenerating.value = false;

                messages.value.push({
                    role: 'assistant',
                    content: data.message,
                    search_results: data.search_results,
                    filters_used: data.filters_used,
                    confirmed: data.confirmed,
                    preview_profiles: data.preview_profiles,
                });

                if (data.filters_used) {
                    currentFilters.value = data.filters_used;
                }
            } else if (
                data.status === 'failed' ||
                data.status === 'budget_exceeded'
            ) {
                stopPolling();
                isGenerating.value = false;
                pushFailureMessage(data.status);
            }
        } catch (e) {
            console.error('Polling failed', e);
        }
    }, 2000);
};

const stopPolling = () => {
    if (pollTimer.value) {
        clearInterval(pollTimer.value);
        pollTimer.value = null;
    }

    activePollConversationId.value = null;
};

const onSearchSelected = (search) => {
    selectedSearch.value = search;
    currentFilters.value = search.filters || {};
};

const toggleArchivedSearches = async () => {
    showArchivedSearches.value = !showArchivedSearches.value;
    selectedSearch.value = null;
    await loadSearches();
};

const setSearchArchived = async (search, archived) => {
    try {
        const response = await fetch(
            route('lead-searches.update', { id: search.id }),
            {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector(
                        'meta[name="csrf-token"]'
                    )?.content,
                },
                body: JSON.stringify({
                    archived,
                }),
            }
        );

        if (!response.ok) {
            return;
        }

        if (selectedSearch.value?.id === search.id) {
            selectedSearch.value = null;
        }

        await loadSearches();
    } catch (e) {
        console.error('Failed to update search archive state', e);
    }
};

const backToChat = () => {
    selectedSearch.value = null;
};

const startNewConversation = async () => {
    stopPolling();
    isGenerating.value = false;
    selectedSearch.value = null;
    try {
        const response = await fetch(props.newConversationUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector(
                    'meta[name="csrf-token"]'
                )?.content,
            },
        });

        const data = await response.json();
        currentConversationId.value = data.id;
        messages.value = [];
        context.value = {};
        currentFilters.value = {};
    } catch (e) {
        console.error('Failed to create new conversation', e);
    }
};

const handlePreview = (previewData) => {
    messages.value.push({
        type: 'preview',
        total: previewData.total,
        profiles: previewData.profiles,
        filters_used: cloneFilters(currentFilters.value),
    });
};

const requestSaveSearch = (filters) => {
    if (filters) {
        currentFilters.value = cloneFilters(filters);
    }

    saveFormRequest.value += 1;
};

const handleSaveSearch = async ({ name, filters, collectCount }) => {
    try {
        const response = await fetch(props.saveSearchUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector(
                    'meta[name="csrf-token"]'
                )?.content,
            },
            body: JSON.stringify({
                name: name,
                filters: filters,
            }),
        });

        if (response.ok) {
            const data = await response.json();
            await loadSearches();

            // Trigger initial collection if count was set
            if (collectCount && collectCount > 0 && data.search?.id) {
                try {
                    const collectResponse = await fetch(
                        route('lead-searches.collect', {
                            searchId: data.search.id,
                        }),
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'X-CSRF-TOKEN': document.querySelector(
                                    'meta[name="csrf-token"]'
                                )?.content,
                            },
                            body: JSON.stringify({ count: collectCount }),
                        }
                    );

                    if (!collectResponse.ok) {
                        const errorData = await collectResponse
                            .json()
                            .catch(() => ({}));
                        messages.value.push({
                            role: 'assistant',
                            content:
                                errorData.error ||
                                _mft('lead-hunter:hugo.errorMessage'),
                        });

                        return;
                    }
                } catch (e) {
                    console.error('Failed to start initial collection', e);

                    messages.value.push({
                        role: 'assistant',
                        content: _mft('lead-hunter:hugo.errorMessage'),
                    });

                    return;
                }

                // Select the new search to show the grid
                const newSearch = savedSearches.value.find(
                    (s) => s.id === data.search.id
                );
                if (newSearch) {
                    selectedSearch.value = newSearch;
                }
            } else {
                await startNewConversation();
            }
        }
    } catch (e) {
        console.error('Failed to save search', e);
    }
};

const onFiltersChanged = (filters) => {
    currentFilters.value = filters;
};

onMounted(() => {
    loadConversation();
    loadSearches();

    appStateStore.setBottomRightToolbarMinimized(true);
});

onUnmounted(() => {
    stopPolling();
});
</script>

<template>
    <div class="flex flex-col h-screen bg-gray-50">
        <!-- Header -->
        <div
            class="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200"
        >
            <div class="flex items-center gap-3">
                <a
                    :href="exitUrl"
                    class="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                            clip-rule="evenodd"
                        />
                    </svg>
                </a>
                <div class="flex items-center gap-2">
                    <div
                        class="w-8 h-8 rounded-full bg-aiAgentLeadFinder flex items-center justify-center"
                    >
                        <ai-agent-lead-finder-avatar class="w-full h-full" />
                    </div>
                    <div>
                        <h1 class="text-sm font-semibold text-gray-900">
                            <mf-translate
                                domainkey="lead-hunter:scout.title"
                            ></mf-translate>
                        </h1>
                        <p class="text-xs text-gray-500 mb-0">
                            <mf-translate
                                domainkey="lead-hunter:scout.subtitle"
                            ></mf-translate>
                        </p>
                    </div>
                </div>
            </div>
            <button-secondary @click="startNewConversation" size="sm">
                <mf-translate
                    domainkey="lead-hunter:scout.newSearch"
                ></mf-translate>
            </button-secondary>
        </div>

        <!-- 3-column layout -->
        <div class="flex flex-1 overflow-hidden">
            <!-- Left: Saved Searches -->
            <div class="w-48 flex-shrink-0 bg-white border-r border-gray-200">
                <search-list
                    :searches="savedSearches"
                    :selected-id="selectedSearch?.id"
                    :showing-archived="showArchivedSearches"
                    :has-lead-hunter-campaigns="hasLeadHunterCampaigns"
                    @refresh="loadSearches"
                    @toggle-archived="toggleArchivedSearches"
                    @archive="setSearchArchived($event, true)"
                    @unarchive="setSearchArchived($event, false)"
                    @select="onSearchSelected"
                ></search-list>
            </div>

            <!-- Center: Chat or Lead Grid -->
            <div class="flex-1 flex flex-col min-w-0">
                <scout-lead-grid
                    v-if="selectedSearch"
                    :search-id="selectedSearch.id"
                    :search-name="selectedSearch.name"
                    @back="backToChat"
                />
                <scout-chat
                    v-else
                    :messages="messages"
                    :is-generating="isGenerating"
                    :initial-message="initialMessage"
                    @send="sendMessage"
                    @save-search="requestSaveSearch"
                ></scout-chat>
            </div>

            <!-- Right: Filters -->
            <div class="w-64 flex-shrink-0 bg-white border-l border-gray-200">
                <scout-filters
                    :filters="currentFilters"
                    :disabled="!!selectedSearch"
                    :search-count-url="searchCountUrl"
                    :locations-url="locationsUrl"
                    :geocode-url="geocodeUrl"
                    :preview-url="previewUrl"
                    :save-form-request="saveFormRequest"
                    @update:filters="onFiltersChanged"
                    @preview="handlePreview"
                    @save="handleSaveSearch"
                ></scout-filters>
            </div>
        </div>
    </div>
</template>
